import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { setSubmission, startStatus, WorkflowError } from '../engine/workflowEngine'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { logWarehouseId, workflowRunner } from './c07.shared'
import { createPickingDiscrepancy } from './discrepancy.handler'
import { categoryOf } from './masterArea.handler'
import { notify, wmsRoute } from './notification.shared'
import { toErrorResponse } from './workflow.handler'

const transferRunner = workflowRunner('WF-TRF-01', 'transfer')

/**
 * Endpoint custom C06 Request Production RM (WF-REQ-01, entity `material_request`):
 * - REQ-01 `create` (SPK / Tembakau; request lahir Draft lalu langsung dikirim `ACT_REQ_SEND` atau
 *   `ACT_REQ_SHORTAGE_PR` bila stok kurang dan dikonfirmasi).
 * - REQ-02A `proposal` (usulan WIP-first FIFO), `issue-picking` (terbitkan tugas + split operator),
 *   `reassign` (atur assignment per unit).
 * - REQ-03 `pick-commit`, `pick-not-found`, `pick-substitute`, `complete-task`, `return-commit`.
 * - REQ-04 `handover-receive` (Terima / Tolak → tugas Return-to-Stock).
 * - REQ-06 `tobacco-recap` (rekap tembakau per mesin dari jejak picking).
 * Ketersediaan stok = saldo unit In Stock di rak − yang sudah dipesan item picking Pending (BA: stok dipesan saat tugas
 * diterbitkan; pemesanan saat submit tidak dimodelkan — dicatat di laporan C06).
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

function fail(message: string, code = 'ERR_VALIDATION_FAILED', status = 400): never {
  throw new WorkflowError(message, code, status)
}

const pad = (value: number, width: number) => String(value).padStart(width, '0')
const round = (value: number) => Math.round(value * 10000) / 10000

export const REQ_WORKFLOW_CODE = 'WF-REQ-01'
/** Area Staging Produksi bawaan (data lama dipetakan ke sini — v7.50 Q-05). */
export const DEFAULT_STAGING_AREA = 'AREA-STG-PRD-1'

/* ------------------------------------------------------------ referensi */

function roleCodesOf(userId: string): string[] {
  return readTable('mt_privileges')
    .filter(row => idOf(row.user) === userId)
    .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
}

function requireRole(message: string, ...roles: string[]) {
  if (!roleCodesOf(currentUserId()).some(code => roles.includes(code))) fail(message, 'ERR_UNAUTHORIZED_ROLE', 403)
}

const workflow = () => {
  const row = readTable('wml_workflow').find(item => item.code === REQ_WORKFLOW_CODE)
  if (!row) fail('Workflow WF-REQ-01 tidak ditemukan.', 'WORKFLOW_NOT_FOUND', 404)
  return row
}

const statusCodeOf = (request: LocalRow) => String(findRow('wml_status', idOf(request.status_id))?.code ?? '')

function requireRequest(requestId: unknown) {
  const request = findRow('material_request', String(requestId ?? ''))
  if (!request) fail('Request tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return request
}

const actionOf = (name: string) => {
  const row = readTable('wml_action').find(item => item.name === name && idOf(item.workflow_id) === workflow().id)
  if (!row) fail(`Action ${name} tidak ditemukan.`, 'ACTION_NOT_FOUND', 404)
  return row
}

function actorFor(...codes: string[]) {
  const roles = readTable('mt_privileges')
    .filter(row => idOf(row.user) === currentUserId())
    .map(row => idOf(row.role))
  const actor = readTable('wml_actor').find(
    row =>
      codes.includes(String(row.code)) && idOf(row.workflow_id) === workflow().id && roles.includes(idOf(row.role_id))
  )
  if (!actor) fail('Actor tidak berhak menjalankan aksi ini.', 'ERR_UNAUTHORIZED_ROLE', 403)
  return actor
}

function runAction(request: LocalRow, name: string, actorCodes: string[], patch: Record<string, unknown> = {}) {
  return setSubmission({
    workflow_id: workflow().id,
    action_id: actionOf(name).id,
    submission_id: idOf(request.submission_id) || null,
    entity_id: request.id,
    actor_id: actorFor(...actorCodes).id,
    reason_text: '',
    native_document_payload: { path: 'material_request', payload: { id: request.id, ...patch } }
  })
}

const GUDANG_ACTORS = ['ACT_REQ_ADMIN_RM', 'ACT_REQ_SPV_RM', 'ACT_REQ_KADIV_RM']

function nextDocNo(prefix: string, collection: string, field: string, date = new Date()) {
  const day = `${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
  const pattern = new RegExp(`^${prefix}-${day}-(\\d{3})`)
  const highest = readTable(collection)
    .map(row => pattern.exec(String(row[field] ?? '')))
    .reduce((max, match) => (match ? Math.max(max, Number(match[1])) : max), 0)
  return `${prefix}-${day}-${pad(highest + 1, 3)}`
}

function txLog(action: string, docRef: string, detail: string, pageCode = 'REQ-02A') {
  insertRow('tx_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    log_type: 'RM Request',
    page_code: pageCode,
    action,
    doc_ref: docRef,
    detail,
    warehouse_id: logWarehouseId(pageCode),
    user_id: currentUserId()
  })
}

const zoneByCode = (code: string) => readTable('warehouse_zone').find(zone => String(zone.code) === code)

/** Area Staging Produksi aktif = zona Master Area berkategori `staging_produksi` (area baru muncul otomatis). */
export function stagingProduksiZones() {
  return readTable('warehouse_zone')
    .filter(zone => zone.is_active !== false && categoryOf(zone) === 'staging_produksi')
    .sort((a, b) => String(a.code).localeCompare(String(b.code), 'en', { numeric: true }))
}

/** Kode Area Staging Produksi yang dipilih Produksi pada request SPK; tembakau tidak punya (tujuan = area mesin). */
export function requestAreaOf(request: LocalRow): string | null {
  const zone = request.staging_zone_id ? findRow('warehouse_zone', idOf(request.staging_zone_id)) : undefined
  return zone ? String(zone.code) : null
}

/** Tujuan "Bawa ke" tugas / serah terima: SPK = area request, tembakau = area mesin. Tanpa fallback ke area tetap. */
function requireRequestArea(request: LocalRow): string {
  const area = requestAreaOf(request)
  if (!area) fail(`Request ${request.req_no} belum punya Area Staging Produksi.`, 'ERR_STAGING_AREA_MISSING', 409)
  return area
}
const rackBinByCode = (code: string) => readTable('mt_rack_bin').find(bin => String(bin.code) === code)
export const rackCodeOf = (rackBinId: unknown) => String(findRow('mt_rack_bin', idOf(rackBinId))?.code ?? '')
const materialOf = (materialId: unknown) => findRow('mt_material', idOf(materialId))
const unitName = (unitId: unknown) => String(findRow('mt_unit', idOf(unitId))?.name ?? '')
const perBigOf = (unit: LocalRow) => Number(unit.small_per_big_snapshot ?? 1) || 1
export const bigQtyOf = (unit: LocalRow) => round(Number(unit.qty_small ?? 0) / perBigOf(unit))

/* ------------------------------------------------------------ stok & usulan */

const linesOf = (request: LocalRow) =>
  readTable('material_request_line').filter(line => idOf(line.request_id) === request.id)

const tasksOf = (request: LocalRow, type = 'REQUEST') =>
  readTable('picking_task').filter(task => task.doc_ref === request.req_no && task.type === type)

const itemsOfTask = (taskId: string) => readTable('picking_task_item').filter(item => idOf(item.task_id) === taskId)

/** Item picking Pending pada tugas yang masih berjalan (bukan Return) = stok terpesan per unit. */
export function reservedByUnit(excludeItemIds: string[] = []) {
  const live = new Set(
    readTable('picking_task')
      .filter(task => task.type !== 'RETURN' && task.task_status !== 'COMPLETED')
      .map(task => task.id)
  )
  const reserved = new Map<string, number>()
  readTable('picking_task_item')
    .filter(item => item.status === 'PENDING' && live.has(idOf(item.task_id)) && !excludeItemIds.includes(item.id))
    .forEach(item => {
      const unit = idOf(item.unit_id)
      reserved.set(unit, round((reserved.get(unit) ?? 0) + Number(item.qty_plan ?? 0)))
    })
  return reserved
}

type StockUnit = {
  unit: LocalRow
  avail: number
  stack: number
  rack: string
  wip: boolean
  qcDate: string
}

const fifoKey = (unit: LocalRow) => String(unit.qc_date ?? unit.stored_at ?? unit.date_created ?? '')

/** Unit In Stock di rak untuk satu material, urut WIP-first lalu FIFO tanggal QC. */
export function stockUnits(materialId: string, reserved = reservedByUnit()): StockUnit[] {
  return readTable('rm_unit')
    .filter(unit => idOf(unit.material_id) === materialId && unit.unit_status === 'IN_STOCK' && unit.rack_bin_id)
    .map(unit => {
      const stack = bigQtyOf(unit)
      return {
        unit,
        stack,
        avail: round(stack - (reserved.get(unit.id) ?? 0)),
        rack: rackCodeOf(unit.rack_bin_id),
        wip: unit.is_leftover_wip === true,
        qcDate: fifoKey(unit)
      }
    })
    .filter(row => row.avail > 0)
    .sort(
      (a, b) =>
        Number(b.wip) - Number(a.wip) ||
        a.qcDate.localeCompare(b.qcDate) ||
        String(a.unit.qr_code).localeCompare(String(b.unit.qr_code))
    )
}

/** Pilihan dropdown Area Staging Produksi pada form SPK (REQ-01) dan filter daftar. */
export function stagingAreas() {
  return {
    items: stagingProduksiZones().map(zone => ({ id: zone.id, code: String(zone.code), name: String(zone.name) }))
  }
}

/** Saldo tersedia (unit besar) per material — kolom TERSEDIA REQ-01. */
export function availability() {
  const reserved = reservedByUnit()
  const byMaterial = new Map<string, { available: number; wip: number }>()
  readTable('rm_unit')
    .filter(unit => unit.unit_status === 'IN_STOCK' && unit.rack_bin_id)
    .forEach(unit => {
      const material = idOf(unit.material_id)
      const entry = byMaterial.get(material) ?? { available: 0, wip: 0 }
      const free = Math.max(0, bigQtyOf(unit) - (reserved.get(unit.id) ?? 0))
      entry.available = round(entry.available + free)
      if (unit.is_leftover_wip === true) entry.wip = round(entry.wip + free)
      byMaterial.set(material, entry)
    })
  return {
    items: [...byMaterial.entries()].map(([material_id, value]) => ({ material_id, ...value }))
  }
}

const plannedOf = (line: LocalRow) =>
  readTable('picking_task_item')
    .filter(item => idOf(item.line_id) === line.id && item.status !== 'NOT_FOUND')
    .reduce((sum, item) => sum + Number(item.qty_plan ?? 0), 0)

type ProposalRow = {
  line_id: string
  material_id: string
  material_code: string
  material_name: string
  unit_name: string
  machine_id: string | null
  unit_id: string
  qr_code: string
  qty: number
  stack_qty: number
  rack: string
  wip: boolean
  qc_date: string
  batch_no: string | null
  alternatives: {
    unit_id: string
    qr_code: string
    stack_qty: number
    avail: number
    rack: string
    wip: boolean
    qc_date: string
  }[]
}

/** Usulan WIP-first FIFO untuk baris yang belum seluruhnya dijadwalkan. Tidak menyimpan apa pun. */
export function proposal(requestId: unknown) {
  const request = requireRequest(requestId)
  const reserved = reservedByUnit()
  const rows: ProposalRow[] = []
  const shortages: { line_id: string; material_code: string; shortage: number }[] = []
  linesOf(request).forEach(line => {
    let need = round(Number(line.qty) - plannedOf(line))
    if (need <= 0) return
    const material = materialOf(line.material_id)
    const candidates = stockUnits(idOf(line.material_id), reserved)
    for (const candidate of candidates) {
      if (need <= 0) break
      const take = Math.min(candidate.avail, need)
      rows.push({
        line_id: line.id,
        material_id: idOf(line.material_id),
        material_code: String(material?.code ?? ''),
        material_name: String(material?.name ?? ''),
        unit_name: unitName(line.unit_id),
        machine_id: idOf(line.machine_id) || null,
        unit_id: candidate.unit.id,
        qr_code: String(candidate.unit.qr_code),
        qty: take,
        stack_qty: candidate.stack,
        rack: candidate.rack,
        wip: candidate.wip,
        qc_date: candidate.qcDate.slice(0, 10),
        batch_no: (candidate.unit.batch_no as string | null) ?? null,
        alternatives: []
      })
      reserved.set(candidate.unit.id, round((reserved.get(candidate.unit.id) ?? 0) + take))
      need = round(need - take)
    }
    if (need > 0) shortages.push({ line_id: line.id, material_code: String(material?.code ?? ''), shortage: need })
  })
  // alternatif unit per baris usulan (untuk "tukar bila perlu")
  const fresh = reservedByUnit()
  rows.forEach(row => {
    row.alternatives = stockUnits(row.material_id, fresh).map(candidate => ({
      unit_id: candidate.unit.id,
      qr_code: String(candidate.unit.qr_code),
      stack_qty: candidate.stack,
      avail: candidate.avail,
      rack: candidate.rack,
      wip: candidate.wip,
      qc_date: candidate.qcDate.slice(0, 10)
    }))
  })
  return { request_id: request.id, rows, shortages }
}

/* ------------------------------------------------------------ REQ-01 create */

type CreateLine = { material_id?: string; machine_id?: string | null; qty?: number; note?: string }
type CreateBody = {
  type?: 'SPK' | 'TOBACCO'
  spk_no?: string
  sku_id?: string
  prod_qty?: number
  /** Kode Area Staging Produksi (Master Area) — wajib untuk SPK; tidak bisa diubah setelah dikirim. */
  staging_area_code?: string
  lines?: CreateLine[]
  confirm_shortage?: boolean
}

export function createRequest(body: CreateBody) {
  requireRole('Request material dibuat oleh Tim Produksi.', 'tim_produksi')
  const type = body.type === 'TOBACCO' ? 'TOBACCO' : 'SPK'
  const spk = String(body.spk_no ?? '').trim()
  if (type === 'SPK' && (!spk || !body.sku_id || !(Number(body.prod_qty) > 0)))
    fail('Header SPK wajib lengkap: No. SPK, Merek, Jumlah Produksi.')
  let stagingZone: LocalRow | undefined
  if (type === 'SPK') {
    const code = String(body.staging_area_code ?? '').trim()
    if (!code) fail('Pilih Area Staging Produksi tujuan antar material.', 'ERR_STAGING_AREA_REQUIRED')
    stagingZone = stagingProduksiZones().find(zone => String(zone.code) === code)
    if (!stagingZone) fail(`${code} bukan Area Staging Produksi yang aktif di Master Area.`, 'ERR_STAGING_AREA_INVALID')
  }
  const merged = new Map<string, { material_id: string; machine_id: string | null; qty: number; note: string }>()
  ;(body.lines ?? []).forEach(line => {
    const qty = Number(line.qty ?? 0)
    if (!line.material_id || !(qty > 0)) return
    if (type === 'TOBACCO' && !line.machine_id) fail('Pilih mesin untuk setiap baris tembakau.')
    const key = `${line.machine_id ?? ''}|${line.material_id}`
    const existing = merged.get(key)
    if (existing) existing.qty = round(existing.qty + qty)
    else
      merged.set(key, {
        material_id: line.material_id,
        machine_id: line.machine_id ?? null,
        qty,
        note: String(line.note ?? '').trim()
      })
  })
  const lines = [...merged.values()]
  if (!lines.length) fail('Minimal satu baris material dengan qty > 0.')
  lines.forEach(line => {
    const material = materialOf(line.material_id)
    if (!material) fail('Material tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    const isTobacco = findRow('mt_material_group', idOf(material.category_id))?.code === 'TEMBAKAU'
    if (type === 'SPK' && isTobacco) fail(`${material.code} adalah tembakau — pakai tab Tembakau.`)
    if (type === 'TOBACCO' && !isTobacco) fail(`${material.code} bukan tembakau.`)
  })

  const stock = new Map(availability().items.map(item => [item.material_id, item.available]))
  const used = new Map<string, number>()
  const shortages = lines.map(line => {
    const free = round((stock.get(line.material_id) ?? 0) - (used.get(line.material_id) ?? 0))
    const covered = Math.max(0, Math.min(line.qty, free))
    used.set(line.material_id, round((used.get(line.material_id) ?? 0) + covered))
    return round(line.qty - covered)
  })
  const hasShortage = shortages.some(value => value > 0)
  if (hasShortage && !body.confirm_shortage)
    fail('Stok tidak mencukupi — konfirmasi submit dengan kekurangan.', 'ERR_SHORTAGE_CONFIRM', 409)

  const start = startStatus(workflow().id)
  const requestId = newId()
  const submission = insertRow('wxl_submission', {
    ...auditFieldsForCreate(),
    id: newId(),
    workflow_id: workflow().id,
    entity: 'material_request',
    entity_id: requestId,
    source: 'ACTION',
    current_status_id: start?.id ?? null,
    last_action_id: null,
    workflow_snapshot: null
  })
  const request = insertRow('material_request', {
    ...auditFieldsForCreate(),
    id: requestId,
    req_no: nextDocNo('REQ', 'material_request', 'req_no'),
    type,
    spk_no: type === 'SPK' ? spk : null,
    sku_id: type === 'SPK' ? (body.sku_id ?? null) : null,
    prod_qty: type === 'SPK' ? Number(body.prod_qty) : null,
    prod_unit_id: null,
    /** v7.50 Q-03: tujuan antar SPK; null untuk tembakau (area mesin). Tidak pernah diubah setelah dikirim. */
    staging_zone_id: stagingZone?.id ?? null,
    has_shortage: false,
    status_id: start?.id ?? null,
    submission_id: submission.id
  })
  lines.forEach((line, index) => {
    insertRow('material_request_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      request_id: requestId,
      material_id: line.material_id,
      machine_id: line.machine_id,
      qty: line.qty,
      unit_id: materialOf(line.material_id)?.default_purchase_unit_id ?? null,
      note: line.note,
      settled_qty: 0,
      is_shortage: shortages[index] > 0,
      shortage_qty: shortages[index]
    })
  })
  runAction(request, hasShortage ? 'ACT_REQ_SHORTAGE_PR' : 'ACT_REQ_SEND', ['ACT_REQ_PRODUKSI'])
  txLog(
    'request-dibuat',
    String(request.req_no),
    type === 'SPK'
      ? `Request SPK ${spk} dibuat — ${lines.length} baris material, tujuan ${stagingZone?.name ?? ''} (${stagingZone?.code ?? ''})${hasShortage ? ', ada kekurangan stok (diteruskan ke Gudang RM)' : ''}`
      : `Permintaan tembakau dibuat — ${lines.length} baris, ${new Set(lines.map(line => line.machine_id)).size} mesin`,
    'REQ-01'
  )
  return { id: requestId, req_no: request.req_no, has_shortage: hasShortage }
}

/* ------------------------------------------------------------ REQ-02A terbitkan & atur */

type IssueBody = {
  request_id?: string
  rows?: { line_id: string; unit_id: string; qty: number }[]
  split?: { operator_id: string; count: number }[]
}

function requireOperator(operatorId: unknown) {
  const operator = findRow('directus_users', String(operatorId ?? ''))
  if (!operator || !roleCodesOf(operator.id).includes('operator_wh_rm')) fail('Pilih operator tujuan dulu.')
  return operator
}

function createTask(request: LocalRow, operatorId: string, machineId: string | null, type = 'REQUEST') {
  const machine = machineId ? findRow('mt_equipment', machineId) : undefined
  // Tugas Return-to-Stock tidak punya tujuan antar; tugas SPK menuju Area Staging Produksi pilihan Produksi (Q-04).
  const areaCode = machine ? `AREA-${String(machine.code)}` : type === 'REQUEST' ? requireRequestArea(request) : null
  return insertRow('picking_task', {
    ...auditFieldsForCreate(),
    id: newId(),
    pick_no: nextDocNo('PICK', 'picking_task', 'pick_no'),
    type,
    doc_ref: request.req_no,
    operator_id: operatorId,
    task_status: 'ASSIGNED',
    machine_id: machineId,
    area_code: areaCode
  })
}

function createItem(task: LocalRow, line: LocalRow, unit: LocalRow, qty: number) {
  return insertRow('picking_task_item', {
    ...auditFieldsForCreate(),
    id: newId(),
    task_id: task.id,
    unit_id: unit.id,
    line_id: line.id,
    qty_plan: qty,
    qty_taken: 0,
    from_rack_bin_id: unit.rack_bin_id,
    from_warehouse_zone_id: unit.warehouse_zone_id ?? null,
    is_wip: unit.is_leftover_wip === true,
    picked_at: null,
    handover_id: null,
    status: 'PENDING'
  })
}

export function issuePicking(body: IssueBody) {
  requireRole(
    'Tugas picking diterbitkan oleh Admin, Supervisor, atau Kadiv WH RM.',
    'admin_wh_rm',
    'wh_rm_supervisor',
    'kadiv_wh_rm'
  )
  const request = requireRequest(body.request_id)
  if (statusCodeOf(request) !== 'REQ_PENDING_ASSIGN')
    fail('Request ini tidak sedang menunggu penugasan.', 'INVALID_FROM_STATUS', 409)
  const rows = (body.rows ?? []).filter(row => Number(row.qty) > 0)
  if (!rows.length) fail('Belum ada unit yang bisa diterbitkan — seluruh barisnya menunggu pengadaan.')
  const split = (body.split ?? []).filter(part => Number(part.count) > 0)
  if (!split.length) fail('Pilih operator tujuan dulu.')
  if (split.reduce((sum, part) => sum + Number(part.count), 0) !== rows.length)
    fail(`Jumlah unit dikerjakan harus sama dengan ${rows.length} unit terpilih.`)
  split.forEach(part => requireOperator(part.operator_id))

  const reserved = reservedByUnit()
  const planned = new Map<string, number>()
  const checked = rows.map(row => {
    const line = findRow('material_request_line', row.line_id)
    const unit = findRow('rm_unit', row.unit_id)
    if (!line || idOf(line.request_id) !== request.id) fail('Baris material bukan bagian request ini.')
    if (
      !unit ||
      unit.unit_status !== 'IN_STOCK' ||
      !unit.rack_bin_id ||
      idOf(unit.material_id) !== idOf(line.material_id)
    )
      fail(`Rekomendasi dihitung ulang: unit ${unit?.qr_code ?? row.unit_id} sudah tidak tersedia — buka ulang usulan.`)
    const free = round(bigQtyOf(unit) - (reserved.get(unit.id) ?? 0) - (planned.get(unit.id) ?? 0))
    if (Number(row.qty) > free)
      fail(`Rekomendasi dihitung ulang: unit ${unit.qr_code} hanya tersisa ${free} — buka ulang usulan.`)
    planned.set(unit.id, round((planned.get(unit.id) ?? 0) + Number(row.qty)))
    return { line, unit, qty: Number(row.qty) }
  })
  const perLine = new Map<string, number>()
  checked.forEach(({ line, qty }) => perLine.set(line.id, round((perLine.get(line.id) ?? 0) + qty)))
  perLine.forEach((qty, lineId) => {
    const line = findRow('material_request_line', lineId) as LocalRow
    if (qty > round(Number(line.qty) - plannedOf(line))) fail('Jumlah melebihi kebutuhan baris material.')
  })

  const taskKey = new Map<string, LocalRow>()
  const created: LocalRow[] = []
  let cursor = 0
  split.forEach(part => {
    checked.slice(cursor, cursor + Number(part.count)).forEach(({ line, unit, qty }) => {
      const machineId = idOf(line.machine_id) || null
      const key = `${part.operator_id}|${machineId ?? ''}`
      let task = taskKey.get(key)
      if (!task) {
        task = createTask(request, part.operator_id, machineId)
        taskKey.set(key, task)
        created.push(task)
      }
      createItem(task, line, unit, qty)
    })
    cursor += Number(part.count)
  })
  runAction(request, 'ACT_REQ_ASSIGN', GUDANG_ACTORS)
  txLog(
    'tugas-diterbitkan',
    String(request.req_no),
    `Tugas picking diterbitkan: ${created.map(task => task.pick_no).join(', ')} (${checked.length} unit)`
  )
  /** Picking ditugaskan → tiap operator mendapat notifikasi nomor tugas + jumlah unitnya. */
  split.forEach(part => {
    const own = created.filter(task => idOf(task.operator_id) === String(part.operator_id))
    notify({
      to: { userIds: [part.operator_id] },
      type: 'PICKING_ASSIGNED',
      title: `Tugas picking ${own.map(task => task.pick_no).join(', ')} untuk Anda`,
      body: `Request ${request.req_no}: ${part.count} unit RM perlu di-pick — buka Picking — Operator.`,
      link: wmsRoute('request-production/picking-rm-operator'),
      ref: { type: 'material_request', id: String(request.id) }
    })
  })
  return { task_nos: created.map(task => task.pick_no), unit_count: checked.length }
}

type ReassignBody = { request_id?: string; assignments?: { item_id: string; operator_id: string }[] }

/** Pindahkan unit (item Pending) antar operator; unit yang sudah dipick terkunci. */
export function reassignPicking(body: ReassignBody) {
  requireRole(
    'Assignment picking diatur oleh Admin, Supervisor, atau Kadiv WH RM.',
    'admin_wh_rm',
    'wh_rm_supervisor',
    'kadiv_wh_rm'
  )
  const request = requireRequest(body.request_id)
  const changes = (body.assignments ?? []).filter(change => change.item_id && change.operator_id)
  let moved = 0
  changes.forEach(change => {
    const item = findRow('picking_task_item', change.item_id)
    const task = item ? findRow('picking_task', idOf(item.task_id)) : undefined
    if (!item || !task || task.doc_ref !== request.req_no) fail('Unit bukan bagian request ini.')
    if (item.status !== 'PENDING') fail('Unit yang sudah dipick terkunci.')
    requireOperator(change.operator_id)
    if (idOf(task.operator_id) === change.operator_id) return
    const machineId = idOf(task.machine_id) || null
    let target = tasksOf(request).find(
      candidate =>
        idOf(candidate.operator_id) === change.operator_id &&
        (idOf(candidate.machine_id) || null) === machineId &&
        candidate.task_status !== 'COMPLETED'
    )
    if (!target) target = createTask(request, change.operator_id, machineId)
    updateRow('picking_task_item', item.id, { task_id: target.id, ...auditFieldsForUpdate() })
    if (!itemsOfTask(task.id).length) deleteRow('picking_task', task.id)
    moved += 1
  })
  if (!moved) fail('Tidak ada unit yang berpindah operator.')
  txLog('tugas-dialihkan', String(request.req_no), `${moved} unit dialihkan ke operator lain`)
  notify({
    to: { userIds: changes.map(change => change.operator_id) },
    type: 'PICKING_ASSIGNED',
    title: `Tugas picking ${request.req_no} dialihkan`,
    body: `Ada unit picking request ${request.req_no} yang dialihkan ke Anda — buka Picking — Operator.`,
    link: wmsRoute('request-production/picking-rm-operator'),
    ref: { type: 'material_request', id: String(request.id) }
  })
  return { moved }
}

/* ------------------------------------------------------------ REQ-03 operator */

function ownTask(taskId: unknown) {
  const task = findRow('picking_task', String(taskId ?? ''))
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (!roleCodesOf(currentUserId()).includes('operator_wh_rm') || idOf(task.operator_id) !== currentUserId())
    fail('Hanya operator pemilik tugas yang bisa scan.', 'ERR_UNAUTHORIZED_ROLE', 403)
  if (task.task_status === 'COMPLETED') fail('Tugas ini sudah selesai.')
  return task
}

function taskItem(task: LocalRow, itemId: unknown) {
  const item = findRow('picking_task_item', String(itemId ?? ''))
  if (!item || idOf(item.task_id) !== task.id) fail('Unit bukan bagian dari tugas ini.', 'ERR_NOT_FOUND', 404)
  return item
}

type CommitBody = {
  task_id?: string
  item_id?: string
  qty_taken?: number
  scanned_rack?: string
  override_reason?: string
  /** Alasan diambil kurang dari rencana (REQ-03 lembar jumlah). */
  short_reason?: string
}

function stockMovement(type: string, unit: LocalRow, qtySmall: number, from: unknown, to: unknown, docRef: string) {
  insertRow('stock_movement', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    movement_type: type,
    unit_id: unit.id,
    qty_small: qtySmall,
    from_rack_bin_id: from ?? null,
    to_rack_bin_id: to ?? null,
    from_warehouse_zone_id: null,
    to_warehouse_zone_id: null,
    doc_ref: docRef
  })
}

export function pickCommit(body: CommitBody) {
  const task = ownTask(body.task_id)
  if (task.type === 'RETURN') fail('Gunakan penyimpanan kembali untuk tugas Return.')
  const item = taskItem(task, body.item_id)
  if (item.status !== 'PENDING') fail('Unit ini sudah diproses.')
  const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
  const qty = Number(body.qty_taken ?? 0)
  const stack = bigQtyOf(unit)
  if (!(qty >= 1) || qty > stack) fail(`Jumlah harus antara 1 dan ${stack}.`)
  const recorded = rackCodeOf(unit.rack_bin_id)
  const scanned = String(body.scanned_rack ?? '').trim()
  const reason = String(body.override_reason ?? '').trim()
  if (scanned && scanned !== recorded && !reason)
    fail('Alasan override wajib diisi — lokasi tidak cocok dengan catatan sistem.')
  const remaining = round(stack - qty)
  updateRow('rm_unit', unit.id, {
    qty_small: round(remaining * perBigOf(unit)),
    ...(remaining === 0 ? { unit_status: 'PICKED', rack_bin_id: null, warehouse_zone_id: null } : {}),
    ...auditFieldsForUpdate()
  })
  updateRow('picking_task_item', item.id, {
    qty_taken: qty,
    status: 'PICKED',
    picked_at: new Date().toISOString(),
    ...(reason ? { override_reason: reason } : {}),
    ...(body.short_reason ? { short_reason: String(body.short_reason).trim() } : {}),
    ...auditFieldsForUpdate()
  })
  stockMovement('RM_PICK', unit, round(qty * perBigOf(unit)), unit.rack_bin_id, null, String(task.pick_no))
  txLog(
    'pick-commit',
    String(unit.qr_code),
    `${unit.qr_code} diambil ${qty} dari ${scanned || recorded} (tugas ${task.pick_no}${remaining > 0 ? `, sisa ${remaining} tetap di rak` : ''})${reason ? ` · override: ${reason}` : ''}${body.short_reason ? ` · kurang dari rencana: ${body.short_reason}` : ''}`,
    'REQ-03'
  )
  return { item_id: item.id, qty_taken: qty, remaining }
}

export function pickNotFound(body: { task_id?: string; item_id?: string; reason?: string }) {
  const task = ownTask(body.task_id)
  const item = taskItem(task, body.item_id)
  if (item.status !== 'PENDING') fail('Unit ini sudah diproses.')
  const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
  updateRow('rm_unit', unit.id, { unit_status: 'MISSING', ...auditFieldsForUpdate() })
  updateRow('picking_task_item', item.id, { status: 'NOT_FOUND', ...auditFieldsForUpdate() })
  /* C13: "Tandai Tidak Ditemukan" membuka selisih Open di papan Discrepancy (BA 00 WF-005). */
  createPickingDiscrepancy({ unit, taskNo: String(task.pick_no), page: 'REQ-03' })
  txLog(
    'pick-not-found',
    String(unit.qr_code),
    `Unit ${unit.qr_code} tidak ditemukan di ${rackCodeOf(item.from_rack_bin_id)} — selisih dicatat untuk Opname (tugas ${task.pick_no})`,
    'REQ-03'
  )
  return { item_id: item.id }
}

/** Ganti unit target dengan unit lain SKU sama (alasan: unit asal tidak ada / rusak). */
export function pickSubstitute(body: { task_id?: string; item_id?: string; unit_id?: string }) {
  const task = ownTask(body.task_id)
  const item = taskItem(task, body.item_id)
  if (item.status !== 'PENDING') fail('Unit ini sudah diproses.')
  const original = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
  const replacement = findRow('rm_unit', String(body.unit_id ?? ''))
  if (!replacement || replacement.unit_status !== 'IN_STOCK' || !replacement.rack_bin_id)
    fail('Unit pengganti tidak tersedia.')
  if (idOf(replacement.material_id) !== idOf(original.material_id)) fail('Unit pengganti harus SKU yang sama.')
  const reserved = reservedByUnit([item.id])
  const free = round(bigQtyOf(replacement) - (reserved.get(replacement.id) ?? 0))
  if (free <= 0) fail('Unit pengganti sudah dipesan tugas lain.')
  updateRow('picking_task_item', item.id, {
    unit_id: replacement.id,
    from_rack_bin_id: replacement.rack_bin_id,
    from_warehouse_zone_id: replacement.warehouse_zone_id ?? null,
    qty_plan: Math.min(Number(item.qty_plan), free),
    is_wip: replacement.is_leftover_wip === true,
    ...auditFieldsForUpdate()
  })
  txLog(
    'pick-substitute',
    String(replacement.qr_code),
    `Unit ${original.qr_code} diganti ${replacement.qr_code} (tugas ${task.pick_no})`,
    'REQ-03'
  )
  return { item_id: item.id, unit_id: replacement.id, qr_code: replacement.qr_code }
}

function createHandover(request: LocalRow, areaCode: string, machineId: string | null, items: LocalRow[]) {
  const handover = insertRow('handover', {
    ...auditFieldsForCreate(),
    id: newId(),
    handover_no: nextDocNo('HND', 'handover', 'handover_no'),
    request_id: request.id,
    machine_id: machineId,
    area_code: areaCode,
    handover_status: 'AWAITING_RECEIPT',
    delivered_at: null,
    received_by: null,
    reject_reason: null
  })
  items.forEach(item => updateRow('picking_task_item', item.id, { handover_id: handover.id }))
  return handover
}

/** Selesaikan tugas: tembakau wajib scan area mesin; SPK → serah terima setelah semua tugas request selesai. */
export function completeTask(body: { task_id?: string; area_code?: string }) {
  const task = ownTask(body.task_id)
  if (task.type === 'RETURN') return completeReturnTask(task)
  if (task.type === 'TRANSFER') return completeTransferTask(task, body.area_code)
  const items = itemsOfTask(task.id)
  if (items.some(item => item.status === 'PENDING')) fail('Masih ada unit yang belum diproses.')
  const picked = items.filter(item => item.status === 'PICKED')
  if (!picked.length) fail('Belum ada tumpukan yang diambil.')
  const request = readTable('material_request').find(row => row.req_no === task.doc_ref) as LocalRow
  const machineId = idOf(task.machine_id) || null
  // Q-04: "Bawa ke" = area request (SPK: Area Staging Produksi pilihan Produksi, tembakau: area mesin). Area lain ditolak.
  const expectedArea = machineId ? String(task.area_code) : requireRequestArea(request)
  const scanned = String(body.area_code ?? '').trim()
  if (scanned.toUpperCase() !== expectedArea.toUpperCase())
    fail(
      scanned
        ? `Area salah — tujuan tugas ini ${expectedArea}. Scan QR area ${expectedArea} untuk menyelesaikan pengambilan.`
        : `Scan QR area ${expectedArea} untuk menyelesaikan pengambilan.`,
      'ERR_WRONG_AREA'
    )
  updateRow('picking_task', task.id, { task_status: 'COMPLETED', ...auditFieldsForUpdate() })
  let handoverNo: string | null = null
  if (machineId) handoverNo = String(createHandover(request, String(task.area_code), machineId, picked).handover_no)
  const siblings = tasksOf(request)
  const allDone = siblings.every(item => item.task_status === 'COMPLETED')
  if (allDone) {
    if (!machineId) {
      const all = siblings.flatMap(sibling => itemsOfTask(sibling.id)).filter(item => item.status === 'PICKED')
      handoverNo = String(createHandover(request, expectedArea, null, all).handover_no)
    }
    runAction(request, 'ACT_REQ_PICK_DONE', ['ACT_REQ_OP_RM'])
  }
  txLog(
    'tugas-selesai',
    String(task.pick_no),
    `Tugas ${task.pick_no} selesai (${picked.length} unit)${handoverNo ? ` — serah terima ${handoverNo} siap` : ''}`,
    'REQ-03'
  )
  return { task_id: task.id, request_ready: allDone, handover_no: handoverNo }
}

/** Tugas Transfer (C07 TRF-01A "Tugaskan Operator"): barang dibawa ke Area Staging `ZS-RM`; tugas terakhir → Siap Kirim. */
function completeTransferTask(task: LocalRow, areaCode?: string) {
  const items = itemsOfTask(task.id)
  if (items.some(item => item.status === 'PENDING')) fail('Masih ada unit yang belum diproses.')
  const picked = items.filter(item => item.status === 'PICKED')
  if (!picked.length) fail('Belum ada tumpukan yang diambil.')
  if (String(areaCode ?? '').trim() !== String(task.area_code))
    fail(`Scan QR area ${task.area_code} untuk menyelesaikan pengambilan.`)
  const staging = zoneByCode(String(task.area_code))
  picked.forEach(item => {
    const unit = findRow('rm_unit', idOf(item.unit_id))
    // Tumpukan terambil penuh (PICKED) ikut ke staging; sisa tumpukan parsial tetap di rak.
    if (unit?.unit_status === 'PICKED')
      updateRow('rm_unit', unit.id, {
        unit_status: 'IN_TRANSFER_STAGING',
        warehouse_zone_id: staging?.id ?? null,
        ...auditFieldsForUpdate()
      })
  })
  updateRow('picking_task', task.id, { task_status: 'COMPLETED', ...auditFieldsForUpdate() })
  const transfer = readTable('transfer').find(row => row.itr_no === task.doc_ref)
  const siblings = readTable('picking_task').filter(row => row.type === 'TRANSFER' && row.doc_ref === task.doc_ref)
  const allDone = siblings.every(row => row.task_status === 'COMPLETED')
  if (transfer && allDone) transferRunner.run(transfer, 'ACT_TRF_STAGING', ['ACT_TRF_OP_RM'])
  txLog(
    'tugas-selesai',
    String(task.pick_no),
    `Tugas Transfer ${task.pick_no} selesai (${picked.length} unit) → ${task.area_code}${allDone ? ' — transfer Siap Kirim' : ''}`,
    'REQ-03'
  )
  return { task_id: task.id, request_ready: allDone, handover_no: null }
}

function completeReturnTask(task: LocalRow) {
  const items = itemsOfTask(task.id)
  if (items.some(item => item.status === 'PENDING')) fail('Masih ada unit yang belum dikembalikan.')
  updateRow('picking_task', task.id, { task_status: 'COMPLETED', ...auditFieldsForUpdate() })
  txLog('return-selesai', String(task.pick_no), `Tugas Return-to-Stock ${task.pick_no} selesai`, 'REQ-03')
  return { task_id: task.id, request_ready: false, handover_no: null }
}

/** Return-to-Stock: unit kembali ke rak (asal atau rak lain dengan konfirmasi). */
export function returnCommit(body: CommitBody) {
  const task = ownTask(body.task_id)
  if (task.type !== 'RETURN') fail('Bukan tugas Return.')
  const item = taskItem(task, body.item_id)
  if (item.status !== 'PENDING') fail('Unit ini sudah dikembalikan.')
  const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow
  const rackCode = String(body.scanned_rack ?? '').trim() || rackCodeOf(item.from_rack_bin_id)
  const bin = rackBinByCode(rackCode)
  if (!bin) fail(`Lokasi ${rackCode} tidak dikenal.`)
  const qty = Number(item.qty_plan)
  updateRow('rm_unit', unit.id, {
    qty_small: round(Number(unit.qty_small ?? 0) + qty * perBigOf(unit)),
    unit_status: 'IN_STOCK',
    rack_bin_id: bin.id,
    warehouse_zone_id: findRow('mt_rack', idOf(bin.rack_id))?.zone_id ?? unit.warehouse_zone_id ?? null,
    ...auditFieldsForUpdate()
  })
  const returnReason = String(body.override_reason ?? '').trim()
  if (rackCode !== rackCodeOf(item.from_rack_bin_id) && !returnReason)
    fail('Alasan wajib diisi — unit dikembalikan ke rak yang berbeda dari asal.')
  updateRow('picking_task_item', item.id, {
    status: 'PICKED',
    qty_taken: qty,
    picked_at: new Date().toISOString(),
    ...(returnReason ? { override_reason: returnReason } : {}),
    ...auditFieldsForUpdate()
  })
  const origin = item.return_of ? findRow('picking_task_item', String(item.return_of)) : undefined
  if (origin)
    updateRow('picking_task_item', origin.id, {
      qty_returned: round(Number(origin.qty_returned ?? 0) + qty),
      ...auditFieldsForUpdate()
    })
  stockMovement('RM_RETURN_TO_STOCK', unit, round(qty * perBigOf(unit)), null, bin.id, String(task.pick_no))
  txLog(
    'return-commit',
    String(unit.qr_code),
    `${unit.qr_code} dikembalikan ${qty} ke ${rackCode} (tugas ${task.pick_no})${returnReason ? ` · rak berbeda: ${returnReason}` : ''}`,
    'REQ-03'
  )
  const done = itemsOfTask(task.id).every(entry => entry.status !== 'PENDING')
  return { item_id: item.id, task_done: done }
}

/* ------------------------------------------------------------ REQ-04 serah terima */

type ReceiveBody = {
  handover_id?: string
  decision?: 'ACCEPT' | 'REJECT'
  reason?: string
  /** QR area yang dipindai Produksi — harus area tujuan serah terima (Q-04). */
  area_code?: string
}

export function receiveHandover(body: ReceiveBody) {
  requireRole(
    body.decision === 'REJECT'
      ? 'Hanya Tim Produksi yang bisa menolak.'
      : 'Hanya Tim Produksi yang bisa mengonfirmasi penerimaan.',
    'tim_produksi'
  )
  const handover = findRow('handover', String(body.handover_id ?? ''))
  if (!handover) fail('Serah terima tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (handover.handover_status !== 'AWAITING_RECEIPT') fail('Serah terima ini sudah diproses.')
  const expectedArea = String(handover.area_code)
  const scanned = String(body.area_code ?? '').trim()
  if (scanned.toUpperCase() !== expectedArea.toUpperCase())
    fail(
      scanned
        ? `Area salah — serah terima ${handover.handover_no} untuk ${expectedArea}.`
        : `Pindai QR area tujuan ${expectedArea} sebelum memproses serah terima.`,
      'ERR_WRONG_AREA'
    )
  const request = requireRequest(idOf(handover.request_id))
  const items = readTable('picking_task_item').filter(item => idOf(item.handover_id) === handover.id)
  if (body.decision === 'REJECT') {
    const reason = String(body.reason ?? '').trim()
    if (!reason) fail('Alasan wajib diisi.')
    updateRow('handover', handover.id, {
      handover_status: 'REJECTED',
      reject_reason: reason,
      ...auditFieldsForUpdate()
    })
    const byOperator = new Map<string, LocalRow[]>()
    items.forEach(item => {
      const operator = idOf(findRow('picking_task', idOf(item.task_id))?.operator_id)
      byOperator.set(operator, [...(byOperator.get(operator) ?? []), item])
    })
    const nos: string[] = []
    byOperator.forEach((group, operator) => {
      const task = createTask(request, operator, null, 'RETURN')
      nos.push(String(task.pick_no))
      group.forEach(origin =>
        insertRow('picking_task_item', {
          ...auditFieldsForCreate(),
          id: newId(),
          task_id: task.id,
          unit_id: origin.unit_id,
          line_id: origin.line_id,
          qty_plan: Number(origin.qty_taken) - Number(origin.qty_returned ?? 0),
          qty_taken: 0,
          from_rack_bin_id: origin.from_rack_bin_id,
          from_warehouse_zone_id: origin.from_warehouse_zone_id ?? null,
          is_wip: origin.is_wip === true,
          return_of: origin.id,
          picked_at: null,
          handover_id: null,
          status: 'PENDING'
        })
      )
    })
    txLog(
      'handover-ditolak',
      String(handover.handover_no),
      `Serah terima ${handover.handover_no} ditolak — ${reason}; tugas Return-to-Stock ${nos.join(', ')}`,
      'REQ-04'
    )
    finishRequestIfResolved(request)
    return { handover_no: handover.handover_no, return_task_nos: nos }
  }
  updateRow('handover', handover.id, {
    handover_status: 'DELIVERED',
    delivered_at: new Date().toISOString(),
    received_by: currentUserId(),
    ...auditFieldsForUpdate()
  })
  items.forEach(item => {
    const unit = findRow('rm_unit', idOf(item.unit_id))
    if (unit?.unit_status === 'PICKED')
      updateRow('rm_unit', unit.id, { unit_status: 'DELIVERED', ...auditFieldsForUpdate() })
    const line = findRow('material_request_line', idOf(item.line_id))
    if (line)
      updateRow('material_request_line', line.id, {
        settled_qty: round(Number(line.settled_qty ?? 0) + Number(item.qty_taken ?? 0)),
        ...auditFieldsForUpdate()
      })
  })
  txLog(
    'handover-diterima',
    String(handover.handover_no),
    `Serah terima ${handover.handover_no} diterima Produksi di ${handover.area_code}`,
    'REQ-04'
  )
  const completed = finishRequestIfResolved(request)
  return { handover_no: handover.handover_no, request_completed: completed }
}

/** Request Completed saat tidak ada serah terima menunggu dan setidaknya satu Delivered. */
function finishRequestIfResolved(request: LocalRow) {
  const handovers = readTable('handover').filter(row => idOf(row.request_id) === request.id)
  const waiting = handovers.some(row => row.handover_status === 'AWAITING_RECEIPT')
  const delivered = handovers.some(row => row.handover_status === 'DELIVERED')
  const tasksDone = tasksOf(request).every(task => task.task_status === 'COMPLETED')
  if (waiting || !delivered || !tasksDone || statusCodeOf(request) !== 'REQ_READY_HANDOVER') return false
  runAction(request, 'ACT_REQ_ACCEPT', ['ACT_REQ_PRODUKSI'])
  return true
}

/* ------------------------------------------------------------ REQ-06 rekap tembakau */

const jakartaDay = (iso: unknown) => {
  const date = new Date(String(iso ?? ''))
  if (Number.isNaN(date.getTime())) return ''
  return new Date(date.getTime() + 7 * 3600_000).toISOString().slice(0, 10)
}

export function tobaccoRecap(date: unknown) {
  const day = String(date ?? '').trim() || jakartaDay(new Date().toISOString())
  const tobacco = readTable('mt_material').filter(
    material => findRow('mt_material_group', idOf(material.category_id))?.code === 'TEMBAKAU'
  )
  const machines = readTable('mt_equipment').filter(machine => machine.is_active !== false)
  const items = readTable('picking_task_item').filter(item => item.status === 'PICKED' && Number(item.qty_taken) > 0)
  const taskOf = (item: LocalRow) => findRow('picking_task', idOf(item.task_id))
  const out = (item: LocalRow) => Number(item.qty_taken) - Number(item.qty_returned ?? 0)
  const requestItems = items.filter(item => taskOf(item)?.type === 'REQUEST')
  return {
    date: day,
    machines: machines.map(machine => ({ id: machine.id, code: machine.code, name: machine.name })),
    rows: tobacco.map(material => {
      const materialUnits = readTable('rm_unit').filter(unit => idOf(unit.material_id) === material.id)
      const unitIds = new Set(materialUnits.map(unit => unit.id))
      const materialItems = requestItems.filter(item => unitIds.has(idOf(item.unit_id)))
      const dayItems = materialItems.filter(item => jakartaDay(item.picked_at) === day)
      const afterItems = materialItems.filter(item => jakartaDay(item.picked_at) > day)
      const perMachine: Record<string, number> = {}
      dayItems.forEach(item => {
        const machine = String(findRow('mt_equipment', idOf(taskOf(item)?.machine_id))?.code ?? '')
        perMachine[machine] = round((perMachine[machine] ?? 0) + out(item))
      })
      const totalOut = round(Object.values(perMachine).reduce((sum, value) => sum + value, 0))
      const inflowOf = (unit: LocalRow) =>
        bigQtyOf(unit) + items.filter(item => idOf(item.unit_id) === unit.id).reduce((sum, item) => sum + out(item), 0)
      const inflow = round(
        materialUnits.filter(unit => jakartaDay(unit.stored_at) === day).reduce((sum, unit) => sum + inflowOf(unit), 0)
      )
      const inflowAfter = round(
        materialUnits.filter(unit => jakartaDay(unit.stored_at) > day).reduce((sum, unit) => sum + inflowOf(unit), 0)
      )
      const stockNow = round(
        materialUnits.filter(unit => unit.unit_status === 'IN_STOCK').reduce((sum, unit) => sum + bigQtyOf(unit), 0)
      )
      const outAfter = round(afterItems.reduce((sum, item) => sum + out(item), 0))
      const closing = round(stockNow + outAfter - inflowAfter)
      const movements = [
        ...materialUnits
          .filter(unit => jakartaDay(unit.stored_at) === day)
          .map(unit => ({
            at: String(unit.stored_at),
            kind: 'MASUK',
            ref: String(unit.qr_code),
            machine: null,
            qty: inflowOf(unit)
          })),
        ...dayItems.map(item => ({
          at: String(item.picked_at),
          kind: 'KELUAR',
          ref: String(taskOf(item)?.pick_no ?? ''),
          machine: String(findRow('mt_equipment', idOf(taskOf(item)?.machine_id))?.code ?? ''),
          qty: out(item)
        }))
      ].sort((a, b) => a.at.localeCompare(b.at))
      return {
        material_id: material.id,
        material_code: material.code,
        material_name: material.name,
        opening: round(closing - inflow + totalOut),
        inflow,
        per_machine: perMachine,
        total_out: totalOut,
        closing,
        movements
      }
    })
  }
}

/* ------------------------------------------------------------ registrasi */

const parse = <T>(data: unknown) => JSON.parse(String(data ?? '{}')) as T

const handle = (run: () => unknown) => {
  try {
    return ok(run())
  } catch (error) {
    return toErrorResponse(error)
  }
}

export function registerMaterialRequestHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/material-request'
  mock.onGet(`${base}/availability`).reply(() => handle(() => availability()))
  mock.onGet(`${base}/staging-areas`).reply(() => handle(() => stagingAreas()))
  mock.onGet(`${base}/proposal`).reply(config => handle(() => proposal(config.params?.request_id)))
  mock.onGet(`${base}/tobacco-recap`).reply(config => handle(() => tobaccoRecap(config.params?.date)))
  mock.onPost(`${base}/create`).reply(config => handle(() => createRequest(parse(config.data))))
  mock.onPost(`${base}/issue-picking`).reply(config => handle(() => issuePicking(parse(config.data))))
  mock.onPost(`${base}/reassign`).reply(config => handle(() => reassignPicking(parse(config.data))))
  mock.onPost(`${base}/pick-commit`).reply(config => handle(() => pickCommit(parse(config.data))))
  mock.onPost(`${base}/pick-not-found`).reply(config => handle(() => pickNotFound(parse(config.data))))
  mock.onPost(`${base}/pick-substitute`).reply(config => handle(() => pickSubstitute(parse(config.data))))
  mock.onPost(`${base}/complete-task`).reply(config => handle(() => completeTask(parse(config.data))))
  mock.onPost(`${base}/return-commit`).reply(config => handle(() => returnCommit(parse(config.data))))
  mock.onPost(`${base}/handover-receive`).reply(config => handle(() => receiveHandover(parse(config.data))))
}
